/**
 * Custom Excel Export Utility for AG Grid
 *
 * This utility provides a vanilla JavaScript solution for exporting AG Grid data to Excel
 * without the tree structure arrows (-> indicators) that appear in the default AG Grid export.
 *
 * Features:
 * - Flattens grouped/tree data structure
 * - Applies borders only to non-empty cells
 * - Maintains proper styling and formatting
 * - Uses SheetJS (xlsx) library for Excel generation
 *
 * Usage:
 * import { exportGridToExcel } from './excelExportUtils';
 * exportGridToExcel(gridApi, 'filename.xlsx', 'Sheet Name');
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { formatDateWithTimezoneOffset } from './utils';
import { reportExceptionToSentry } from '../services/sentry/utils';

interface ExcelExportOptions {
  fileName: string;
  sheetName: string;
  data: any[];
  columns: { field: string; headerName: string }[];
  colDefs?: any[];
  timezoneOffset?: string;
}

interface CellStyle {
  border?: {
    top?: { style: string; color: { rgb: string } };
    bottom?: { style: string; color: { rgb: string } };
    left?: { style: string; color: { rgb: string } };
    right?: { style: string; color: { rgb: string } };
  };
  fill?: {
    fgColor: { rgb: string };
  };
  font?: {
    bold?: boolean;
    size?: number;
    name?: string;
  };
  alignment?: {
    horizontal?: string;
    vertical?: string;
  };
}

/**
 * Flattens AG Grid row data by removing tree structure indicators
 * and extracting actual data values. Works with both client-side and server-side row models.
 */
export function flattenGridData(gridApi: any): any[] {
  const flattenedData: any[] = [];

  // For server-side row model (which is what this grid uses)
  // We'll use getDisplayedRowCount and getDisplayedRowAtIndex
  // instead of trying to detect the row model type

  try {
    const displayedRowCount = gridApi.getDisplayedRowCount();

    if (displayedRowCount === 0) {
      console.warn('No displayed rows to export');
      return flattenedData;
    }

    for (let i = 0; i < displayedRowCount; i++) {
      const node = gridApi.getDisplayedRowAtIndex(i);
      if (node) {
        const cleanData = extractCleanRowData(node, gridApi);
        if (Object.keys(cleanData).length > 0) {
          flattenedData.push(cleanData);
        }
      }
    }
  } catch (error) {
    console.error('Error flattening grid data:', error);
    // Fallback: try the forEach method if available
    if (gridApi.forEachNode) {
      gridApi.forEachNode((node: any) => {
        const cleanData = extractCleanRowData(node, gridApi);
        if (Object.keys(cleanData).length > 0) {
          flattenedData.push(cleanData);
        }
      });
    }
  }

  return flattenedData;
}

/**
 * Extracts clean data from a row node, removing tree structure indicators
 * and properly handling hierarchical row groups with parent values repeated in child rows
 */
function extractCleanRowData(node: any, gridApi: any): any {
  const cleanData: any = {};

  // Get row group columns to understand the hierarchy
  const rowGroupCols = gridApi.getRowGroupColumns ? gridApi.getRowGroupColumns() : [];

  // Get displayed columns to know which fields to include
  const displayedColumns = gridApi.getAllDisplayedColumns();

  // Build the full hierarchy path by traversing up the parent chain
  if (rowGroupCols.length > 0) {
    // Collect all parent values by traversing up the tree
    const hierarchyPath: { [key: string]: any } = {};
    let currentNode = node;

    // Traverse up the parent chain to collect all group values
    while (currentNode) {
      if (currentNode.group && currentNode.field) {
        // Find the column definition for this group level
        const groupCol = rowGroupCols.find(
          (col: any) => col.getColDef().field === currentNode.field
        );
        if (groupCol) {
          const headerName = groupCol.getColDef().headerName || currentNode.field;
          // Only set if not already set (child values take precedence)
          if (!hierarchyPath[headerName]) {
            hierarchyPath[headerName] = currentNode.key || '';
          }
        }
      }
      currentNode = currentNode.parent;
    }

    // Also handle groupData which contains parent group information
    if (node.groupData) {
      Object.keys(node.groupData).forEach((groupField) => {
        const groupCol = rowGroupCols.find((col: any) => col.getColDef().field === groupField);
        if (groupCol) {
          const headerName = groupCol.getColDef().headerName || groupField;
          hierarchyPath[headerName] = node.groupData[groupField];
        }
      });
    }

    // If this is a group node, add its own group value
    if (node.group && node.field) {
      const groupCol = rowGroupCols.find((col: any) => col.getColDef().field === node.field);
      if (groupCol) {
        const headerName = groupCol.getColDef().headerName || node.field;
        hierarchyPath[headerName] = node.key || '';
      }
    }

    // For leaf nodes (non-group rows), extract parent values from the path
    if (!node.group && node.data) {
      // Walk up the parent chain to get all group values
      let parentNode = node.parent;
      while (parentNode && parentNode.group) {
        if (parentNode.field) {
          const groupCol = rowGroupCols.find(
            (col: any) => col.getColDef().field === parentNode.field
          );
          if (groupCol) {
            const headerName = groupCol.getColDef().headerName || parentNode.field;
            if (!hierarchyPath[headerName]) {
              hierarchyPath[headerName] = parentNode.key || '';
            }
          }
        }
        parentNode = parentNode.parent;
      }
    }

    // Add all hierarchy values to cleanData
    Object.keys(hierarchyPath).forEach((key) => {
      cleanData[key] = hierarchyPath[key];
    });
  }

  displayedColumns.forEach((column: any) => {
    const colId = column.getColId();
    const field = column.getColDef().field;
    const headerName = column.getColDef().headerName || colId;

    let cellValue: any;

    // Skip auto-group column as we've handled groups above
    if (colId === 'ag-Grid-AutoColumn') {
      return;
    }

    if (field) {
      // Handle regular data columns
      if (node.data && node.data[field] !== undefined) {
        cellValue = node.data[field];
      } else if (node.aggData && node.aggData[field] !== undefined) {
        // Handle aggregated data for group rows
        cellValue = node.aggData[field];
      }

      // Use the header name as key
      cleanData[headerName] = cellValue;
    }
  });

  return cleanData;
}

/**
 * Checks if a cell value is considered "non-empty" for styling purposes
 */
function isNonEmptyCell(value: any): boolean {
  return value !== null && value !== undefined && value !== '' && value !== 0;
}

/**
 * Creates Excel styles for headers and data cells
 */
function createCellStyles(): { [key: string]: CellStyle } {
  return {
    header: {
      fill: {
        fgColor: { rgb: 'F8F9FA' }
      },
      font: {
        bold: true,
        size: 12,
        name: 'Arial'
      },
      alignment: {
        horizontal: 'center',
        vertical: 'center'
      },
      border: {
        top: { style: 'thin', color: { rgb: 'DEE2E6' } },
        bottom: { style: 'thin', color: { rgb: 'DEE2E6' } },
        left: { style: 'thin', color: { rgb: 'DEE2E6' } },
        right: { style: 'thin', color: { rgb: 'DEE2E6' } }
      }
    },
    nonEmptyCell: {
      border: {
        top: { style: 'thin', color: { rgb: 'E9ECEF' } },
        bottom: { style: 'thin', color: { rgb: 'E9ECEF' } },
        left: { style: 'thin', color: { rgb: 'E9ECEF' } },
        right: { style: 'thin', color: { rgb: 'E9ECEF' } }
      },
      alignment: {
        vertical: 'center'
      },
      font: {
        size: 11,
        name: 'Arial'
      }
    },
    emptyCell: {
      alignment: {
        vertical: 'center'
      },
      font: {
        size: 11,
        name: 'Arial'
      }
    }
  };
}

/**
 * Formats a cell value based on column type
 */
function formatCellValue(
  value: any,
  headerName: string,
  colDefs?: any[],
  timezoneOffset?: string
): any {
  if (!colDefs || !timezoneOffset || value === null || value === undefined || value === '') {
    return value ?? '';
  }

  // Find the column definition for this field
  const colDef = colDefs.find((def) => {
    const defHeaderName = def.headerName || def.field;
    return defHeaderName === headerName;
  });

  // If column type is dateTime, format it
  if (colDef && colDef.type === 'dateTime') {
    try {
      return formatDateWithTimezoneOffset(value, timezoneOffset);
    } catch (error) {
      console.error('Error formatting date-time value:', error);
      return value;
    }
  }

  return value;
}

/**
 * Exports flattened grid data to Excel with styling
 */
export function exportToExcel(options: ExcelExportOptions): void {
  const { fileName, sheetName, data, columns, colDefs, timezoneOffset } = options;
  const offset = data[0].offset || timezoneOffset;
  if (
    data.length > 0 &&
    colDefs?.length &&
    colDefs.find((col) => col.type === 'dateTime') &&
    !data[0].offset
  ) {
    console.error('Offset is not found in the data for column with type dateTime');
    reportExceptionToSentry({
      title: 'Extended-A SSRM Report Export Error',
      message:
        // eslint-disable-next-line max-len
        'Offset is not found in the data for column with type dateTime. Now using company timezone offset instead.',
      stack: new Error().stack as any,
      tags: {
        fileName,
        url: window.location.href
      }
    });
  }

  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();

  // Prepare headers
  const headers = columns.map((col) => col.headerName || col.field);
  const fields = columns.map((col) => col.field);

  // Create worksheet data with headers and data rows
  // Format date-time values during data mapping
  const worksheetData = [
    headers,
    ...data.map((row) =>
      fields.map((field) => {
        const headerName = columns.find((col) => col.field === field)?.headerName || field;
        const value = row[field];
        return formatCellValue(value, headerName, colDefs, offset);
      })
    )
  ];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Apply styling
  const styles = createCellStyles();
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

  // Initialize cell styles object
  if (!worksheet['!styles']) {
    worksheet['!styles'] = {};
  }

  // Style header row with bold font
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!worksheet[cellAddress]) {
      worksheet[cellAddress] = { t: 's', v: '' };
    }
    // Apply header style with bold font
    worksheet[cellAddress].s = {
      ...styles.header,
      font: {
        ...styles.header.font,
        bold: true
      }
    };
  }

  // Style data rows
  for (let row = 1; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cellValue = worksheet[cellAddress]?.v;

      if (!worksheet[cellAddress]) {
        worksheet[cellAddress] = { t: 's', v: '' };
      }

      // Apply style based on whether cell has meaningful data
      if (isNonEmptyCell(cellValue)) {
        worksheet[cellAddress].s = styles.nonEmptyCell;
      } else {
        worksheet[cellAddress].s = styles.emptyCell;
      }
    }
  }

  // Set column widths
  const columnWidths = columns.map(() => ({ wch: 15 }));
  worksheet['!cols'] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true
  });

  // Save file
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  saveAs(blob, fileName);
}

/**
 * Exports AG Grid data to Excel with flattened structure and styling
 */
/**
 * Exports AG Grid data to CSV with flattened structure
 */
export function exportGridToCSV(
  gridApi: any,
  fileName: string,
  colDefs?: any[],
  timezoneOffset?: string
): void {
  try {
    // Validate gridApi
    if (!gridApi) {
      throw new Error('Grid API is not available');
    }

    console.log('Starting CSV export...');

    // Get flattened data
    const flattenedData = flattenGridData(gridApi);

    if (flattenedData.length === 0) {
      console.warn('No data to export');
      alert('No data available to export. Please ensure the grid has loaded data.');
      return;
    }

    console.log(`Exporting ${flattenedData.length} rows to CSV`);

    // Get row group columns and displayed columns
    const rowGroupCols = gridApi.getRowGroupColumns ? gridApi.getRowGroupColumns() : [];
    const displayedColumns = gridApi.getAllDisplayedColumns();
    const columns: { field: string; headerName: string }[] = [];

    // First, add row group columns in order (parent before child)
    rowGroupCols.forEach((col: any) => {
      const field = col.getColDef().field;
      const headerName = col.getColDef().headerName || field;
      columns.push({
        field: headerName,
        headerName: headerName
      });
    });

    // Then add the remaining data columns
    const sampleRow = flattenedData[0];
    Object.keys(sampleRow).forEach((key) => {
      // Skip if this is already added as a row group column
      if (columns.some((col) => col.field === key)) {
        return;
      }

      // Find the corresponding column definition
      const matchingCol = displayedColumns.find((col: any) => {
        const colId = col.getColId();
        const field = col.getColDef().field;
        const headerName = col.getColDef().headerName || colId;

        return key === headerName || key === field;
      });

      columns.push({
        field: key,
        headerName: matchingCol ? matchingCol.getColDef().headerName || matchingCol.getColId() : key
      });
    });

    console.log(`Total columns before validation: ${columns.length}`);

    // Validate and remove empty columns
    const nonEmptyColumns = columns.filter((column) => {
      // Check if at least one row has a non-empty value for this column
      const hasData = flattenedData.some((row) => {
        const value = row[column.field];
        return value !== null && value !== undefined && value !== '';
      });
      return hasData;
    });

    console.log(`Non-empty columns after validation: ${nonEmptyColumns.length}`);

    if (nonEmptyColumns.length === 0) {
      console.warn('No non-empty columns found after validation');
      alert('No data columns found to export.');
      return;
    }

    // Filter the data to only include non-empty columns
    const cleanedData = flattenedData.map((row) => {
      const cleanRow: any = {};
      nonEmptyColumns.forEach((column) => {
        cleanRow[column.field] = row[column.field];
      });
      return cleanRow;
    });

    // Build CSV content
    const headers = nonEmptyColumns.map((col) => col.headerName);
    const csvRows = [headers];

    // Add data rows
    cleanedData.forEach((row) => {
      const rowData = nonEmptyColumns.map((col) => {
        const value = row[col.field];
        // Handle null/undefined
        if (value === null || value === undefined) {
          return '';
        }
        // Format date-time values if applicable
        const formattedValue = formatCellValue(value, col.headerName, colDefs, timezoneOffset);
        // Convert to string and escape quotes
        const stringValue = String(formattedValue);
        // If value contains comma, quotes, or newlines, wrap in quotes and escape existing quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csvRows.push(rowData);
    });

    // Convert to CSV string
    const csvContent = csvRows.map((row) => row.join(',')).join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName);

    console.log('CSV export completed successfully');
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw error;
  }
}

export function exportGridToExcel(
  gridApi: any,
  fileName: string,
  sheetName: string = 'Data',
  colDefs?: any[],
  timezoneOffset?: string
): void {
  try {
    // Validate gridApi
    if (!gridApi) {
      throw new Error('Grid API is not available');
    }

    // Log available methods for debugging
    console.log('Grid API methods available:', Object.keys(gridApi));

    // Get flattened data first to determine what columns we actually have
    const flattenedData = flattenGridData(gridApi);

    if (flattenedData.length === 0) {
      console.warn('No data to export');
      alert('No data available to export. Please ensure the grid has loaded data.');
      return;
    }

    console.log(`Exporting ${flattenedData.length} rows to Excel`);

    // Get row group columns and displayed columns
    const rowGroupCols = gridApi.getRowGroupColumns ? gridApi.getRowGroupColumns() : [];
    const displayedColumns = gridApi.getAllDisplayedColumns();
    const columns: { field: string; headerName: string }[] = [];

    // First, add row group columns in order (parent before child)
    rowGroupCols.forEach((col: any) => {
      const field = col.getColDef().field;
      const headerName = col.getColDef().headerName || field;
      columns.push({
        field: headerName,
        headerName: headerName
      });
    });

    // Then add the remaining data columns
    const sampleRow = flattenedData[0];
    Object.keys(sampleRow).forEach((key) => {
      // Skip if this is already added as a row group column
      if (columns.some((col) => col.field === key)) {
        return;
      }

      // Find the corresponding column definition
      const matchingCol = displayedColumns.find((col: any) => {
        const colId = col.getColId();
        const field = col.getColDef().field;
        const headerName = col.getColDef().headerName || colId;

        return key === headerName || key === field;
      });

      columns.push({
        field: key,
        headerName: matchingCol ? matchingCol.getColDef().headerName || matchingCol.getColId() : key
      });
    });

    console.log(`Total columns before validation: ${columns.length}`);

    // Validate and remove empty columns
    const nonEmptyColumns = columns.filter((column) => {
      // Check if at least one row has a non-empty value for this column
      const hasData = flattenedData.some((row) => {
        const value = row[column.field];
        return value !== null && value !== undefined && value !== '';
      });
      return hasData;
    });

    console.log(`Non-empty columns after validation: ${nonEmptyColumns.length}`);

    if (nonEmptyColumns.length === 0) {
      console.warn('No non-empty columns found after validation');
      alert('No data columns found to export.');
      return;
    }

    // Filter the data to only include non-empty columns
    const cleanedData = flattenedData.map((row) => {
      const cleanRow: any = {};
      nonEmptyColumns.forEach((column) => {
        cleanRow[column.field] = row[column.field];
      });
      return cleanRow;
    });

    // Export to Excel
    exportToExcel({
      fileName,
      sheetName,
      data: cleanedData,
      columns: nonEmptyColumns,
      colDefs,
      timezoneOffset
    });

    console.log('Excel export completed successfully');
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
}
